<script setup lang="ts">
import { computed } from 'vue';
import { ArrowRightIcon, ChevronDownIcon } from '@heroicons/vue/24/solid';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useInspectorSections } from '@/composables/useInspectorSections';
import { useIssueCommandForm } from '@/composables/useIssueCommandForm';
import LzpCommandChooser from './LzpCommandChooser.vue';
import LzpSchedulePicker from './LzpSchedulePicker.vue';

/**
 * Single-player "Issue Command" control for the inspector's Commands & Logs tab.
 * Reuses the same logic as the bulk bar via {@link useIssueCommandForm} — here with a
 * one-element target list — so the command catalogue, raw mode, client gating,
 * scheduling, and the issue + result toast behave identically. On success the chosen
 * command resets and the pending-commands list refetches (the mutation invalidates its
 * query key). Open state remembered per entity.
 */
const props = defineProps<{ player: Record<string, unknown> }>();

const sections = useInspectorSections('LocationZonePlayer');

const {
    enumsQuery,
    issueMutation,
    selectedCommand,
    canIssueRawCommand,
    rawMode,
    rawCommand,
    resetRawCommand,
    scheduleDate,
    scheduleTime,
    minScheduleDate,
    usePlayerTime,
    browserTimezone,
    browserTimezoneAbbreviation,
    eligibility,
    selectCommand,
    optionGateReason,
    issuePayloadCommand,
    commandLabel,
    scheduledAt,
    scheduledInPast,
    scheduleLabel,
    clearSchedule,
    issue,
} = useIssueCommandForm(() => [props.player]);

// One target → surface its single client-side gate reason (if any) to disable + explain.
const gateReason = computed(() => eligibility.value.ineligible[0]?.reason ?? null);
const canIssue = computed(
    () =>
        issuePayloadCommand.value !== null &&
        !issueMutation.isPending.value &&
        !scheduledInPast.value &&
        gateReason.value === null
);

function submit(): void {
    if (!canIssue.value) {
        return;
    }
    // Reset the picked command AND the schedule after a successful issue so a scheduled
    // command doesn't leave its date/time in place for the next one.
    issue(() => {
        selectedCommand.value = null;
        resetRawCommand();
        clearSchedule();
    });
}
</script>

<template>
    <Collapsible
        :open="sections.isOpen('Issue Command')"
        class="border-border/80 bg-muted/20 rounded-lg border px-3 py-2"
        data-testid="lzp-inspector-issue-command"
        @update:open="sections.setOpen('Issue Command', $event)"
    >
        <CollapsibleTrigger
            class="group focus-visible:ring-ring flex w-full items-center justify-between gap-2 text-left outline-none focus-visible:ring-1"
        >
            <h4 class="text-sm font-semibold">Issue Command</h4>
            <ChevronDownIcon
                class="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
                aria-hidden="true"
            />
        </CollapsibleTrigger>
        <CollapsibleContent>
            <div class="mt-2 flex flex-wrap items-center gap-2 text-sm">
                <LzpCommandChooser
                    v-model:raw-mode="rawMode"
                    v-model:raw-command="rawCommand"
                    :commands="enumsQuery.data.value ?? []"
                    :loading="enumsQuery.isLoading.value"
                    :selected="selectedCommand"
                    :can-issue-raw="canIssueRawCommand"
                    :option-gate-reason="optionGateReason"
                    test-id-prefix="lzp-issue"
                    @select="selectCommand"
                    @enter="submit"
                />

                <!-- Schedule chip: "now" = issue immediately; a pick opens the calendar + time + player-time toggle. -->
                <LzpSchedulePicker
                    v-model:date="scheduleDate"
                    v-model:time="scheduleTime"
                    v-model:use-player-time="usePlayerTime"
                    :min-value="minScheduleDate"
                    :scheduled-at="scheduledAt"
                    :scheduled-in-past="scheduledInPast"
                    :schedule-label="scheduleLabel"
                    :browser-timezone="browserTimezone"
                    :browser-timezone-abbreviation="browserTimezoneAbbreviation"
                    test-id-prefix="lzp-issue-schedule"
                />

                <button
                    type="button"
                    class="bg-primary text-primary-foreground hover:bg-primary/90 ml-auto inline-flex size-7 shrink-0 items-center justify-center rounded-md disabled:opacity-50"
                    :disabled="!canIssue"
                    data-testid="lzp-issue-submit"
                    :aria-label="`Issue ${commandLabel || 'command'} to this player`"
                    :title="issueMutation.isPending.value ? 'Issuing…' : 'Issue'"
                    @click="submit"
                >
                    <ArrowRightIcon class="size-4" aria-hidden="true" />
                </button>
            </div>

            <!-- Client preview of the server gating; the issue button is disabled while this shows. -->
            <p v-if="gateReason" class="text-muted-foreground mt-2 text-xs" data-testid="lzp-issue-gate-reason">
                {{ gateReason }}
            </p>
        </CollapsibleContent>
    </Collapsible>
</template>
